/**
 * Orca v3 — the fields of a run proposal: what a person asked for, refined into one run of a loop version
 * before it starts. The client contracts use these alone; the row models are in `V3Proposal.ts`, which
 * re-exports them.
 *
 * A **proposal** belongs to one person (who will start it) and one loop version. Its **revisions** are
 * append-only: each holds the whole payload and its digest. A digest proves only that a payload is the one
 * shown — never that anyone agreed to it. Its **material** — the request, inputs and scope changes — is what
 * an approval agrees to: a revision that changes scope or acceptance criteria, or changes material an earlier
 * approval agreed to, needs its person's explicit **approval** of that material. Implementation context (where
 * the code is, what was tried) is untrusted evidence, added without asking.
 *
 * Everything here is public to the customer's members and bounded; it never holds a credential.
 *
 * @since 1.0.0
 */
import { Schema } from "effect";
import { V3Params } from "./V3Loop.ts";

const text = (max: number) =>
  Schema.String.pipe(
    Schema.filter((value) => value.trim() !== "", { message: () => "must not be blank" }),
    Schema.maxLength(max),
  );

/** A condition's id within its loop version. */
const conditionRef = Schema.String.pipe(Schema.pattern(/^[a-z0-9][a-z0-9_-]{0,63}$/));

/**
 * A revision's longest plain request, finding, answer or refinement text.
 *
 * @since 1.0.0
 */
export const V3_PROPOSAL_TEXT_MAX = 2_000;

/**
 * Most entries in each list of a revision.
 *
 * @since 1.0.0
 */
export const V3_PROPOSAL_LIST_MAX = 30;

/**
 * Most revisions of one proposal: past them, a person makes a new proposal. Approvals are bounded with them
 * (one per revision, by its person), so whatever reads a proposal's history reads a bounded amount.
 *
 * @since 1.0.0
 */
export const V3_PROPOSAL_REVISIONS_MAX = 50;

/**
 * An earlier run, by its id — an id and nothing else.
 *
 * @since 1.0.0
 */
export const V3RunReference = Schema.String.pipe(
  Schema.pattern(/^[0-9A-HJKMNP-TV-Z]{26}$/, { message: () => "must be a run id" }),
);

/**
 * Something intake looked at — a file, a resource, an earlier run — and what it found.
 *
 * @since 1.0.0
 */
export const V3ProposalFinding = Schema.Struct({
  source: text(300),
  finding: text(V3_PROPOSAL_TEXT_MAX),
});
export type V3ProposalFinding = typeof V3ProposalFinding.Type;

/**
 * A focused question intake asked its person, and their answer.
 *
 * @since 1.0.0
 */
export const V3ProposalQuestion = Schema.Struct({
  question: text(V3_PROPOSAL_TEXT_MAX),
  answer: text(V3_PROPOSAL_TEXT_MAX),
});
export type V3ProposalQuestion = typeof V3ProposalQuestion.Type;

/**
 * Implementation context for one condition's executor: where the work is, what to build on. It changes
 * neither scope nor acceptance criteria, so it needs no approval.
 *
 * @since 1.0.0
 */
export const V3ProposalContext = Schema.Struct({
  conditionId: conditionRef,
  text: text(V3_PROPOSAL_TEXT_MAX),
});
export type V3ProposalContext = typeof V3ProposalContext.Type;

/**
 * A change to one condition's scope or acceptance: another success criterion it must meet, or new
 * instructions. Each needs its person's approval of the revision.
 *
 * @since 1.0.0
 */
export const V3ScopeChange = Schema.Struct({
  conditionId: conditionRef,
  change: Schema.Literal("add_criterion", "replace_instructions"),
  text: text(V3_PROPOSAL_TEXT_MAX),
  /** Why, in the person's terms. */
  reason: text(600),
});
export type V3ScopeChange = typeof V3ScopeChange.Type;

const list = <A, I>(item: Schema.Schema<A, I>) => Schema.Array(item).pipe(Schema.maxItems(V3_PROPOSAL_LIST_MAX));

/**
 * One revision's whole payload.
 *
 * @since 1.0.0
 */
export const V3ProposalPayload = Schema.Struct({
  /** The person's request, verbatim. */
  request: text(4_000),
  /** This run's input values, by the loop version's input ids. */
  inputs: V3Params,
  inspected: list(V3ProposalFinding),
  questions: list(V3ProposalQuestion),
  context: list(V3ProposalContext),
  scope: list(V3ScopeChange),
  /** Earlier runs of the loop it drew on. */
  pastRuns: Schema.Array(V3RunReference).pipe(Schema.maxItems(20)),
}).annotations({ parseOptions: { onExcessProperty: "error" } });
export type V3ProposalPayload = typeof V3ProposalPayload.Type;

/**
 * Where a proposal stands: `open` (being refined or awaiting its start), `started` (a run was started from
 * one of its revisions; frozen), `withdrawn`.
 *
 * @since 1.0.0
 */
export const V3ProposalStatus = Schema.Literal("open", "started", "withdrawn");
export type V3ProposalStatus = typeof V3ProposalStatus.Type;

/**
 * Where a revision came from: a coding agent's MCP session, or the web app.
 *
 * @since 1.0.0
 */
export const V3ProposalOrigin = Schema.Literal("mcp", "web");
export type V3ProposalOrigin = typeof V3ProposalOrigin.Type;

/**
 * The refinements a run started from a proposal runs with: implementation context and the approved scope
 * changes, applied over its loop version's conditions.
 *
 * @since 1.0.0
 */
export const V3ProposalRefinements = Schema.Struct({
  context: Schema.Array(V3ProposalContext),
  scope: Schema.Array(V3ScopeChange),
});
export type V3ProposalRefinements = typeof V3ProposalRefinements.Type;
